import { useQuery, useApolloClient, NetworkStatus } from '@apollo/client'
import format from 'date-fns/format'
import isValid from 'date-fns/isValid'
import max from 'date-fns/max'
import { isEmpty } from 'lodash'
import { createContext, ReactElement, useEffect, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { Content, ContentHeader, ContentBody } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { DepartureDateFilter } from 'features/Trips/views/Index/DepartureDateFilter'
import { GET_TRIPS } from 'features/Trips/queries'
import { useCompanyLinesAutocompleteQuery } from 'features/Lines/hooks'
import { ICompanyLine } from 'features/Lines/types'
import { TRIPS_LIMIT } from 'features/Trips/constants'
import { resolvers } from 'resolvers/trips'
import { initDateByString, DATE_FORMAT } from 'utils/date'
import { filterEmptyValue } from 'utils/object'
import { getQueries } from 'utils/route'
import { stringIsBoolean, stringToBoolean } from 'utils/boolean'
import { TBooleanStr } from 'utils/boolean'
import { canRead, canCreateTrip } from 'features/Users/utils'
import { TripFilters, TripV2VehicleStatus } from './Filters'
import { TripsList } from './List'
import { TripsPagination } from './Pagination'
import { ISelectedTripsContext } from 'features/Trips/types'
import { useLogin } from 'features/Login/hooks'
import { TripDrawer } from 'features/Trips/components/TripDrawer'
import { IPartnerAutocomplete } from 'features/Partners/components/MultiPartnerAutocomplete'
import {
  GTDriverSuggestionsResponse,
  GTPartnerSuggestionsResponse,
  GTRouteServiceSuggestionsResponse,
  GTRouteSuggestion,
  GTRouteSuggestionsResponse,
  GTTripV2,
  GTVehicleSuggestionsResponse
} from 'gql/types.generated'
import { GET_DRIVERS_SUGGESTIONS } from 'features/Drivers/queries-legacy'
import { usePlace } from 'features/Places/hooks'
import { useCity } from 'features/Cities/hooks'
import { useCompanyAutocomplete } from 'features/Companies/hooks'
import { useBusinessClientAutocomplete } from 'features/BusinessClients/hooks'
import { useBusinessSiteAutocomplete } from 'features/BusinessSites/hooks'
import { endOfDay, startOfDay } from 'date-fns'
import { GET_ROUTE_SERVICES_AUTOCOMPLETE } from 'features/Services/queries-legacy'
import { GET_PARTNERS_SUGGESTIONS } from 'features/Partners/queries-legacy'
import { GET_VEHICLES_SUGGESTIONS } from 'features/Vehicles/queries-legacy'
import { getShortId } from 'utils/string'
import { WITHOUT_DRIVER } from 'features/Drivers/components/DriverAutocomplete'
import { GET_ROUTES_SUGGESTIONS } from 'features/Routes/queries-legacy'
import { convertHour24ToHour12Time, sanitizeTimeRange } from 'utils/time'
import {
  TripsV2DayOfWeek,
  TripsV2SaleStatusInput,
  TripsV2StatusInput,
  TripV2DriverRsvpStatus
} from 'gql/generated/graphql'

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px 32px;
`

const messages = defineMessages({
  helmet: {
    id: 'trips.helmet',
    defaultMessage: 'Trips | Nexbus Admin'
  }
})

const sanitizeDepartureDateFrom = (departureDateFromStr: string): Date => {
  const today = new Date()
  const departureDateFrom = initDateByString(departureDateFromStr)

  if (isValid(departureDateFrom)) {
    return departureDateFrom as Date
  }

  return today
}

const sanitizeDepartureDateTo = (departureDateToStr: string, departureDateFrom: Date): Date => {
  const today = new Date()
  const departureDateTo = initDateByString(departureDateToStr)

  if (isValid(departureDateTo)) {
    return max([departureDateTo as Date, departureDateFrom])
  }

  return today
}

const sanitizeCompanyLine = (companyLineId: string, companyLines: ICompanyLine[]): TSFixMe => {
  const companyLine = companyLines.find((companyLine): TSFixMe => companyLine.id === companyLineId)
  return {
    id: companyLine?.id || '',
    name: companyLine?.name || ''
  }
}

const getTripsQuery = ({ filters, departureDateFrom, departureDateTo }: TSFixMe): TSFixMe => {
  return {
    departureDateFrom: format(departureDateFrom, DATE_FORMAT),
    departureDateTo: format(departureDateTo, DATE_FORMAT),
    companyId: filters.company?.id,
    companyLine: filters.companyLine && filters.companyLine.id,
    originCityId: filters.originCity && filters.originCity.id,
    destinationCityId: filters.destinationCity && filters.destinationCity.id,
    routeServiceIds: filters.routeServices.map(({ id }: { id: string }) => id).join(','),
    originPlaceId: filters.origin && filters.origin.id,
    destinationPlaceId: filters.destination && filters.destination.id,
    vehicle: filters.vehicle && filters.vehicle.id,
    vehicleStatus: filters.vehicleStatus,
    status: filters.status,
    saleStatus: filters.saleStatus,
    hasIncidences: filters.hasIncidences,
    ids: filters.ids.map((id: string) => getShortId(id)).join(','),
    partnersIds: filters.partners.map((partner: IPartnerAutocomplete) => partner.id).join(','),
    driverId: filters.driver?.id,
    routeIds: filters.routes.map((route: GTRouteSuggestion) => route.id).join(','),
    businessClientId: filters.businessClient?.id,
    businessSiteId: filters.businessSite?.id,
    departureDaysOfWeek: filters.departureDaysOfWeek.join(','),
    departureStartHour: filters.departureHourRange?.startHour,
    departureEndHour: filters.departureHourRange?.endHour,
    driverRSVPStatus: filters.driverRSVPStatus
  }
}

export const SelectedTripsContext = createContext<ISelectedTripsContext>({
  selectedTripId: undefined,
  selectedTrips: [],
  setSelectedTrips: (): void => {
    // do nothing yet
  },
  setSelectedTripId: (): void => {
    // do nothing yet
  }
})

const TripsView = (): ReactElement => {
  const location = useLocation()
  const intl = useIntl()
  const login = useLogin()
  const client = useApolloClient()
  const [searchParams] = useSearchParams()

  let content
  const queries = getQueries(location.search)
  const {
    departureDateFrom = '',
    departureDateTo = '',
    companyLine = '',
    originCityId = '',
    destinationCityId = '',
    routeServiceIds = '',
    originPlaceId = '',
    destinationPlaceId = '',
    vehicle = '',
    vehicleStatus = '',
    status = '',
    saleStatus = '',
    page = '1',
    hasIncidences = '',
    ids = '',
    partnersIds = '',
    driverId = '',
    companyId = '',
    routeIds = '',
    businessClientId = '',
    businessSiteId = '',
    departureDaysOfWeek = '',
    departureStartHour = '',
    departureEndHour = ''
  } = queries
  const driverRSVPStatus = searchParams.get('driverRSVPStatus') || ''
  const [selectedTripId, setSelectedTripId] = useState<string | undefined>(undefined)
  const [selectedTrips, setSelectedTrips] = useState<GTTripV2[]>([])
  const { data: vehiclesData } = useQuery<{ vehicleSuggestions: GTVehicleSuggestionsResponse }>(
    GET_VEHICLES_SUGGESTIONS,
    {
      variables: {
        input: {
          id: vehicle
        }
      },
      skip: !vehicle
    }
  )

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const sanitizedDepartureDateFrom = sanitizeDepartureDateFrom(departureDateFrom)
  const sanitizedDepartureDateTo = sanitizeDepartureDateTo(departureDateTo, sanitizedDepartureDateFrom)
  const sanitizedDepartureHourRange = sanitizeTimeRange({
    startHour: departureStartHour,
    endHour: departureEndHour,
    defaultTimeRange: {
      startHour: '00:00',
      endHour: '23:59'
    }
  })
  const pageNb = parseInt(page)
  const routeServiceIdsList = routeServiceIds ? routeServiceIds.split(',') : []
  const idsList = ids ? ids.split(',') : []
  const partnersIdsList = partnersIds ? partnersIds.split(',') : []
  const routesIdsList = routeIds ? routeIds.split(',') : []
  const departureDaysOfWeekList = departureDaysOfWeek ? departureDaysOfWeek.split(',') : []
  client.addResolvers(resolvers)

  const originPlace = usePlace(originPlaceId, { skip: !originPlaceId })
  const destinationPlace = usePlace(destinationPlaceId, { skip: !destinationPlaceId })
  const originCity = useCity(originCityId, { skip: !originCityId })
  const destinationCity = useCity(destinationCityId, { skip: !destinationCityId })
  const companyAutocomplete = useCompanyAutocomplete(companyId, { skip: !companyId })
  const businessClientAutocomplete = useBusinessClientAutocomplete(businessClientId, { skip: !businessClientId })
  const businessSiteAutocomplete = useBusinessSiteAutocomplete(businessSiteId, { skip: !businessSiteId })
  const { data: partnersData } = useQuery<{ partnerSuggestions: GTPartnerSuggestionsResponse }>(
    GET_PARTNERS_SUGGESTIONS,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: isEmpty(partnersIdsList),
      variables: {
        input: {
          ids: partnersIdsList
        }
      }
    }
  )
  const { data: routesData } = useQuery<{ routeSuggestions: GTRouteSuggestionsResponse }>(GET_ROUTES_SUGGESTIONS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: isEmpty(routesIdsList),
    variables: {
      input: {
        ids: routesIdsList
      }
    }
  })
  const { data: driversData } = useQuery<{
    driverSuggestions: GTDriverSuggestionsResponse
  }>(GET_DRIVERS_SUGGESTIONS, {
    fetchPolicy: 'network-only',
    skip: isEmpty(driverId) || driverId === WITHOUT_DRIVER.id,
    variables: {
      input: {
        id: driverId
      }
    }
  })
  const { companyLines } = useCompanyLinesAutocompleteQuery()
  const { data: routeServicesData } = useQuery<{ routeServiceSuggestions: GTRouteServiceSuggestionsResponse }>(
    GET_ROUTE_SERVICES_AUTOCOMPLETE,
    {
      variables: {
        input: {
          ids: routeServiceIdsList
        }
      },
      skip: isEmpty(routeServiceIdsList)
    }
  )

  const { loading, error, data, refetch, networkStatus } = useQuery(GET_TRIPS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        page: pageNb,
        size: TRIPS_LIMIT
      },
      input:
        filterEmptyValue({
          departureAtRange: {
            startAt: startOfDay(sanitizedDepartureDateFrom).toISOString(),
            endAt: endOfDay(sanitizedDepartureDateTo).toISOString()
          },
          companyID: companyId,
          companyLineID: companyLine,
          originCityID: originCityId,
          destinationCityID: destinationCityId,
          routeServiceIDs: routeServiceIdsList,
          originID: originPlaceId,
          destinationID: destinationPlaceId,
          vehicleID: vehicle,
          vehicleIsActive: vehicleStatus ? vehicleStatus === TripV2VehicleStatus.Active : undefined,
          status: status as TripsV2StatusInput,
          saleStatus: saleStatus as TripsV2SaleStatusInput,
          hasIncidents: hasIncidences ? stringToBoolean(hasIncidences as TBooleanStr) : undefined,
          shortIDs: idsList.map((id: string) => getShortId(id)),
          partnerIDs: partnersIdsList,
          driverID: driverId === WITHOUT_DRIVER.id ? undefined : driverId,
          routeIDs: routesIdsList,
          businessClientID: businessClientId,
          businessSiteID: businessSiteId,
          hasDriver: driverId === WITHOUT_DRIVER.id ? false : undefined,
          departureDaysOfWeek: departureDaysOfWeekList as TripsV2DayOfWeek[],
          departureHourRange:
            sanitizedDepartureHourRange.startHour || sanitizedDepartureHourRange.endHour
              ? {
                  startHour: convertHour24ToHour12Time(sanitizedDepartureHourRange.startHour) || undefined,
                  endHour: convertHour24ToHour12Time(sanitizedDepartureHourRange.endHour) || undefined
                }
              : undefined,
          driverRSVPStatus: driverRSVPStatus as TripV2DriverRsvpStatus
        }) || {}
    }
  })

  useEffect(() => {
    setSelectedTrips([])
  }, [location.search])

  const filters = {
    ids: idsList,
    company: companyAutocomplete,
    companyLine: sanitizeCompanyLine(companyLine, companyLines),
    origin: {
      id: originPlace?.id || '',
      name: originPlace?.name || ''
    },
    destination: {
      id: destinationPlace?.id || '',
      name: destinationPlace?.name || ''
    },
    originCity: {
      id: originCity?.id || '',
      name: originCity?.name || ''
    },
    destinationCity: {
      id: destinationCity?.id || '',
      name: destinationCity?.name || ''
    },
    routeServices: routeServicesData?.routeServiceSuggestions.items || [],
    vehicle: vehiclesData?.vehicleSuggestions?.items.at(0),
    vehicleStatus,
    status,
    saleStatus,
    hasIncidences: hasIncidences && stringIsBoolean(hasIncidences) ? hasIncidences : undefined,
    partners: partnersData?.partnerSuggestions?.items || [],
    driver: driverId === WITHOUT_DRIVER.id ? WITHOUT_DRIVER : driversData?.driverSuggestions?.items.at(0),
    routes: routesData?.routeSuggestions?.items || [],
    businessClient: {
      id: businessClientAutocomplete?.id || '',
      name: businessClientAutocomplete?.name || ''
    },
    businessSite: {
      id: businessSiteAutocomplete?.id || '',
      name: businessSiteAutocomplete?.name || ''
    },
    departureDaysOfWeek: departureDaysOfWeekList,
    departureHourRange: sanitizedDepartureHourRange,
    driverRSVPStatus
  }

  useEffect(() => {
    refetch()
  }, [])

  if (!canRead(FEATURES.TRIP, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.TRIP} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="trips.error.description" defaultMessage="There was an error while loading your trips" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="trips.error.action" defaultMessage="Reload your trips" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.tripsV2.items) && pageNb === 1 && isEmpty(filterEmptyValue(filters))) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="trips.empty.description" defaultMessage="You don't have any trips yet" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/routes" kind="primary">
            <FormattedMessage id="trips.empty.action" defaultMessage="Add a service first" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.tripsV2.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="trips.empty.filters.description"
            defaultMessage="There was no trips found according to your filters"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/trips" kind="primary">
            <FormattedMessage id="trips.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <TripsList trips={data?.tripsV2.items as GTTripV2[]} />
        <TripsPagination
          page={pageNb}
          totalItems={data?.tripsV2.total}
          limit={TRIPS_LIMIT}
          currentItemsCount={data?.tripsV2.items.length}
          filters={filters}
          departureDateFrom={sanitizedDepartureDateFrom}
          departureDateTo={sanitizedDepartureDateTo}
          getTripsQuery={getTripsQuery}
        />
      </>
    )
  }

  return (
    <SelectedTripsContext.Provider
      value={{
        selectedTripId,
        selectedTrips,
        setSelectedTrips,
        setSelectedTripId
      }}
    >
      <Content>
        <ContentHeader isResponsive>
          <Heading.H3>
            <FormattedMessage id="trips.header.label" defaultMessage="Trips" />
          </Heading.H3>
          <HeaderActions>
            <DepartureDateFilter
              departureDateFrom={sanitizedDepartureDateFrom}
              departureDateTo={sanitizedDepartureDateTo}
              filters={filters}
              getTripsQuery={getTripsQuery}
            />
            {canCreateTrip(login.accessToken.policies) && (
              <Button as={NavLink} to="/trips/create" kind="secondary">
                <FormattedMessage id="trips.header.create" defaultMessage="Create trip" />
              </Button>
            )}
          </HeaderActions>
        </ContentHeader>
        <ContentBody>
          <TripFilters
            filters={filters}
            departureDateFrom={sanitizedDepartureDateFrom}
            departureDateTo={sanitizedDepartureDateTo}
            getTripsQuery={getTripsQuery}
          />
          <section>{content}</section>
        </ContentBody>
      </Content>
      <TripDrawer />
    </SelectedTripsContext.Provider>
  )
}

export default withLastUrl(TripsView)
